import { Check, ChevronDown, Search, X } from 'lucide-react';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface FormSelectProps {
  label?: string;
  error?: FieldError | string;
  helperText?: string;
  required?: boolean;
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const FormSelect = forwardRef<HTMLInputElement, FormSelectProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      options,
      value,
      onChange,
      placeholder = 'Select an option...',
      searchable = false,
      clearable = false,
      disabled = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const inputId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = typeof error === 'string' ? error : error?.message;
    const hasError = !!errorMessage;

    const selectedOption = options.find(option => option.value === value);

    // Filter options based on search term
    useEffect(() => {
      if (!searchTerm) {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(
          options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      setHighlightedIndex(-1);
    }, [searchTerm, options]);

    // Handle clicks outside component
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearchTerm('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when opened
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
        setSearchTerm('');
      }
    };

    const handleOptionSelect = (optionValue: string) => {
      onChange?.(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            handleOptionSelect(filteredOptions[highlightedIndex].value);
          } else if (!isOpen) {
            setIsOpen(true);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSearchTerm('');
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
          }
          break;
      }
    };

    return (
      <div className="space-y-1">
        {/* Label */}
        {label && (
          <label htmlFor={inputId} className="block text-meta font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Select Container */}
        <div className="relative" ref={containerRef}>
          {/* Hidden input for form integration */}
          <input
            ref={ref}
            type="hidden"
            id={inputId}
            value={value || ''}
            aria-invalid={hasError}
            aria-describedby={
              errorMessage ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />

          {/* Display Button */}
          <button
            type="button"
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={`
              relative w-full h-10 px-3 py-2 text-left border border-gray-300 rounded-lg shadow-sm
              bg-white text-body-sm
              focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400
              disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
              ${hasError ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
              ${className}
            `}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            role="combobox"
          >
            <span
              className={`block truncate ${!selectedOption ? 'text-gray-500' : 'text-gray-900'}`}
            >
              {selectedOption?.label || placeholder}
            </span>

            {/* Icons */}
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
              {clearable && value && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Clear selection"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </span>
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
              {/* Search Input */}
              {searchable && (
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search options..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 text-body-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
                    />
                  </div>
                </div>
              )}

              {/* Options List */}
              <ul ref={listRef} className="max-h-60 overflow-auto py-1" role="listbox">
                {filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-body-sm text-gray-500">No options found</li>
                ) : (
                  filteredOptions.map((option, index) => (
                    <li
                      key={option.value}
                      role="option"
                      aria-selected={option.value === value}
                      className={`
                        relative cursor-pointer select-none px-3 py-2 text-body-sm
                        ${option.disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-900'}
                        ${highlightedIndex === index ? 'bg-brand-50 text-brand-900' : ''}
                        ${option.value === value ? 'bg-brand-100 text-brand-900' : ''}
                        hover:bg-brand-50
                      `}
                      onClick={() => !option.disabled && handleOptionSelect(option.value)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span className="block truncate">{option.label}</span>
                      {option.value === value && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <Check className="h-4 w-4 text-brand-600" />
                        </span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Helper Text */}
        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="text-meta text-gray-500">
            {helperText}
          </p>
        )}

        {/* Error Message */}
        {hasError && (
          <p id={`${inputId}-error`} className="text-meta text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
